import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'

export function ActivityControls({ runId, candidates, filter, setFilter, onCandidates }: {
  runId: string, candidates: any[], filter: string, setFilter: (s: string) => void, onCandidates: (v: any[]) => void
}) {
  const [state, setState] = useState<any>({ status: 'unchecked', counts: {} })
  const [error, setError] = useState('')
  const [starting, setStarting] = useState(false)
  useEffect(() => {
    let cancelled = false, timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const data = await api.getActivity(runId)
        if (cancelled) return
        setState(data); onCandidates(data.candidates || candidates); setError(data.error || '')
        if (['queued', 'running'].includes(data.status)) timer = setTimeout(poll, 2000)
      } catch (e) { if (!cancelled) { setError((e as Error).message); timer = setTimeout(poll, 5000) } }
    }
    poll()
    return () => { cancelled = true; clearTimeout(timer) }
  }, [runId, starting, candidates, onCandidates])
  const start = async (action: string) => {
    if (action === 'refresh' && !window.confirm('Check everyone again using this run’s LinkedIn account? Existing activity results will be backed up.')) return
    setStarting(true); setError('')
    try { const data = await api.startActivity(runId, action); setState(data); onCandidates(data.candidates || candidates) }
    catch (e) { setError((e as Error).message) }
    finally { setStarting(false) }
  }
  const busy = starting || ['running', 'queued'].includes(state.status)
  return <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
    <div className="flex flex-wrap items-center justify-between gap-3"><div><h3 className="text-sm font-medium">Profile activity</h3><p className="text-xs text-slate-500">Active: an accessible post within 90 days. Viewing or filtering never starts a check.</p></div><div className="flex gap-2">
      {!state.collection_id && <Button size="sm" variant="outline" disabled={busy} onClick={() => start('check')}>Check activity</Button>}
      {state.collection_id && !['completed', 'complete'].includes(state.status) && <Button size="sm" variant="outline" disabled={busy} onClick={() => start('resume')}>Resume</Button>}
      {state.collection_id && <Button size="sm" variant="outline" disabled={busy} onClick={() => start('refresh')}>Refresh activity</Button>}
    </div></div>
    {busy && <div><div className="mb-1 text-xs text-slate-500">{state.processed || 0}/{state.total || candidates.length} checked</div><div className="h-1.5 overflow-hidden rounded bg-slate-200"><div className="h-full bg-blue-500" style={{ width: `${Math.min(100, (state.processed || 0) / (state.total || candidates.length || 1) * 100)}%` }} /></div></div>}
    <div className="flex flex-wrap gap-2">{['all', 'active', 'inactive', 'unknown'].map(value => <button key={value} onClick={() => setFilter(value)} className={`rounded-md px-2.5 py-1 text-xs capitalize ${filter === value ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>{value} · {state.counts?.[value] ?? (value === 'all' || value === 'unknown' ? candidates.length : 0)}</button>)}</div>
    <p className="text-xs text-slate-500">Unknown includes {state.counts?.unchecked ?? candidates.length} unchecked and {state.counts?.error || 0} failed lookups. The selected filter also applies to XLSX.</p>
    {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
  </div>
}
